import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ticketsApi } from '../api'
import {
  Alert,
  Button,
  EmptyState,
  Input,
  Loader,
  PageHeader,
  Pagination,
  Table,
  Toolbar,
} from '../ui'
import { HiArrowPath, HiEye, HiInbox, HiMagnifyingGlass } from 'react-icons/hi2'

const PER_PAGE = 20

export default function Data({ hideHeader }) {
  const navigate = useNavigate()
  const [tickets, setTickets] = useState([])
  const [total, setTotal]     = useState(0)
  const [page, setPage]       = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)
  const [search, setSearch]   = useState('')

  const load = useCallback(async (silent = false) => {
    if (!silent) { setLoading(true); setError(null) }
    try {
      const params = {
        skip: (page - 1) * PER_PAGE, limit: PER_PAGE,
        status: 'resolved',
      }
      if (search) params.search = search
      const res = await ticketsApi.list(params)
      setTickets(res.items || [])
      setTotal(res.total || 0)
    } catch (e) { if (!silent) setError(e.message) }
    finally { if (!silent) setLoading(false) }
  }, [page, search])

  useEffect(() => { load() }, [load])

  // Arxivni jimgina avtomatik yangilash
  useEffect(() => {
    const id = setInterval(() => { load(true) }, 8000)
    return () => clearInterval(id)
  }, [load])

  const columns = [
    {
      key: 'ticket_number',
      title: 'Murojaat №',
      width: '12%',
      render: t => (
        <span className="ui-mono ui-nums" style={{ fontSize: 'var(--ui-fs-sm)', fontWeight: 600 }}>
          #{t.ticket_number}
        </span>
      ),
    },
    {
      key: 'citizen',
      title: 'Fuqaro',
      width: '22%',
      render: t => (
        <div className="ui-col ui-gap-4" style={{ minWidth: 0 }}>
          <span className="ui-truncate" style={{ fontSize: 'var(--ui-fs-sm)', fontWeight: 500 }}>
            {t.citizen?.full_name || t.citizen_name || 'Fuqaro'}
          </span>
          {t.citizen?.username && (
            <span className="ui-mono ui-muted ui-truncate" style={{ fontSize: 'var(--ui-fs-xs)' }}>
              @{t.citizen.username}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'description',
      title: 'Muammo',
      width: '36%',
      render: t => (
        <div className="ui-truncate" style={{ maxWidth: 320, fontSize: 'var(--ui-fs-xs)', color: 'var(--ui-text-2)' }}>
          {t.description}
        </div>
      ),
    },
    {
      key: 'updated_at',
      title: 'Tugatilgan vaqt',
      width: '18%',
      render: t => (
        <span className="ui-nums ui-muted" style={{ fontSize: 'var(--ui-fs-xs)', whiteSpace: 'nowrap' }}>
          {t.updated_at ? new Date(t.updated_at).toLocaleString('uz-UZ', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
          }) : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      title: 'Amallar',
      align: 'right',
      render: t => (
        <Button
          variant="secondary"
          size="sm"
          icon={HiEye}
          onClick={e => { e.stopPropagation(); navigate(`/ticket/${t.id}`) }}
        >
          Ko'rish
        </Button>
      ),
    },
  ]

  return (
    <div className={hideHeader ? '' : 'page-enter'}>
      {!hideHeader && (
        <PageHeader
          title="Ma'lumotlar bazasi"
          sub="Yakunlangan barcha suhbatlar arxivi"
          actions={
            <Button
              variant="ghost"
              size="sm"
              icon={HiArrowPath}
              iconOnly
              aria-label="Yangilash"
              title="Yangilash"
              onClick={() => load()}
            />
          }
        />
      )}

      {error && (
        <div style={{ marginBottom: 'var(--ui-sp-16)' }}>
          <Alert tone="danger" title="Ma'lumotni yuklab bo'lmadi">
            <div className="ui-between ui-gap-12 ui-wrap">
              <span style={{ fontSize: 'var(--ui-fs-xs)' }}>{error}</span>
              <Button variant="secondary" size="sm" icon={HiArrowPath} onClick={() => load()}>
                Qayta urinish
              </Button>
            </div>
          </Alert>
        </div>
      )}

      {/* Qidiruv */}
      <div style={{ marginBottom: 'var(--ui-sp-16)' }}>
        <Toolbar>
          <div className="ui-grow" style={{ minWidth: 200, maxWidth: 480 }}>
            <Input
              icon={HiMagnifyingGlass}
              placeholder="Murojaat №, ism, username yoki matn..."
              aria-label="Arxivda qidirish"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
            />
          </div>
          {/* Arxiv qidirilmoqda/filtrlanmoqda — "searching" holati.
              Jami soni joyida qoladi, shuning uchun tartib sakramaydi */}
          {loading && <Loader state="searching" size="sm" label={false} />}
          <span className="ui-muted" style={{ fontSize: 'var(--ui-fs-xs)' }}>
            Jami: <span className="ui-nums" style={{ color: 'var(--ui-text-1)', fontWeight: 600 }}>{total}</span> ta yozuv
          </span>
        </Toolbar>
      </div>

      {/* Jadval */}
      <Table
        columns={columns}
        rows={tickets}
        loading={loading}
        onRowClick={t => navigate(`/ticket/${t.id}`)}
        empty={
          <EmptyState
            icon={HiInbox}
            title={search ? 'Qidiruv natijalari topilmadi' : "Arxiv bo'sh"}
            text={search
              ? "Siz kiritgan so'rovga mos arxivlangan suhbat topilmadi."
              : 'Hozircha tizimda arxivlangan (tugatilgan) suhbatlar mavjud emas.'}
          />
        }
      />

      {total > PER_PAGE && (
        <div style={{ marginTop: 'var(--ui-sp-12)' }}>
          <Pagination total={total} page={page} perPage={PER_PAGE} onChange={setPage} />
        </div>
      )}
    </div>
  )
}
